import { FormsLayoutComponent } from '@modules/forms/components/forms-layout.component';
import { LazySpaceChatPanelComponent } from './components/dynamic-panel/lazy-space-chat-panel.component';
import { LazyStitchyPanelComponent } from './components/dynamic-panel/lazy-stitchy-panel.component';
// Common
import { NgModule } from '@angular/core';

// Modules
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CalendarAppModule } from '@modules/calendar-app/calendar-app.module';
import { ContactsModule } from '@modules/contacts/contacts.module';
import { ElementsModule } from '@modules/elements/elements.module';
import { FilesModule } from '@modules/files/files.module';
import { FormControlsModule } from '@modules/form-controls/form-controls.module';
import { FullCalendarModule } from '@modules/full-calendar/full-calendar.module';
import { KnotsModule } from '@modules/knots/knots.module';
import { KnowledgeModule } from '@modules/knowledge/knowledge.module';
import { MessagesModule } from '@modules/messages/messages.module';
import { NotesModule } from '@modules/notes/notes.module';
import { ImportDialogComponent } from '@modules/sheets/import-dialog.component';
import { SheetsModule } from '@modules/sheets/sheets.module';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { NoContentStubComponent } from '@frontendlib/components/components/no-content-stub/no-content-stub.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { SearchModule } from '@modules/search/search.module';
import { SplitViewModule } from '@modules/split-view/split-view.module';
import { UtilsModule } from '@modules/utils/utils.module';
import { CommonModule as AppCommonModule } from '../common/common.module';
import { UserAvatarModule } from '../user-avatar/user-avatar.module';
import { TasksModule } from './../tasks/tasks.module';

// Components
import { StitchyLauncherComponent } from '@modules/chat/components/stitchy-launcher/stitchy-launcher.component';
import { CalendarSearchPopoverComponent } from '@modules/search/components/calendar-search-popover/calendar-search-popover.component';
import { ContactsSearchPopoverComponent } from '@modules/search/components/contacts-search-popover/contacts-search-popover.component';
import { FilesSearchPopoverComponent } from '@modules/search/components/files-search-popover/files-search-popover.component';
import { MailSearchPopoverComponent } from '@modules/search/components/mail-search-popover/mail-search-popover.component';
import { NotesSearchPopoverComponent } from '@modules/search/components/notes-search-popover/notes-search-popover.component';
import { SheetsSearchPopoverComponent } from '@modules/search/components/sheets-search-popover/sheets-search-popover.component';
import { SearchStateIndicatorComponent } from '@modules/search/components/search-state-indicator/search-state-indicator.component';
import { TaskingSearchPopoverComponent } from '@modules/search/components/tasking-search-popover/tasking-search-popover.component';
import { ContactsContextMenuComponent } from './components/contacts-context-menu/contacts-context-menu.component';
import { ContactsPanelComponent } from './components/contacts-panel/contacts-panel.component';
import { DynamicPanelComponent } from './components/dynamic-panel/dynamic-panel.component';
import { EventsContextMenuComponent } from './components/events-context-menu/events-context-menu.component';
import { EventsPanelComponent } from './components/events-panel/events-panel.component';
import { FilesContextMenuComponent } from './components/files-context-menu/files-context-menu.component';
import { FilesPanelComponent } from './components/files-panel/files-panel.component';
import { FormsContainerComponent } from './components/forms-container/forms-container.component';
import { MessagesContextMenuComponent } from './components/messages-context-menu/messages-context-menu.component';
import { MessagesPanelComponent } from './components/messages-panel/messages-panel.component';
import { NotesContextMenuComponent } from './components/notes-context-menu/notes-context-menu.component';
import { NotesPanelComponent } from './components/notes-panel/notes-panel.component';
import { SheetsContextMenuComponent } from './components/sheets-context-menu/sheets-context-menu.component';
import { NavAddMenuComponent } from '@modules/pages/components/nav-add-menu/nav-add-menu.component';
import { PanelSheetCreateComponent } from '@modules/sheets/components/panel-sheet-create/panel-sheet-create.component';
import { SheetsPanelComponent } from './components/sheets-panel/sheets-panel.component';
import { SheetsPanelWorkbookComponent } from './components/sheets-panel/sheets-panel-workbook.component';
import { BoardsPanelComponent } from './components/boards-panel/boards-panel.component';
import { AtlasPanelOutletComponent } from '@modules/atlas/panel/atlas-panel-outlet.component';
import { LazySlidesViewComponent } from '@modules/slides/components/lazy-slides-view/lazy-slides-view.component';
import { TasksContextMenuComponent } from './components/tasks-context-menu/tasks-context-menu.component';
import { TasksPanelComponent } from './components/tasks-panel/tasks-panel.component';

// Directives
import { StickyEventsListComponent } from '@modules/calendar-app/components/sticky-events-list/sticky-events-list.component';
import { CalendarAppContextDirective } from '@modules/calendar-app/directives/app-calendar-app-context.directive';
import { ContactsAppContextDirective } from '@modules/contacts/directives/app-contacts-app-context.directive';
import { FolderQuickFormComponent } from '@modules/elements/components/quick-forms/folder-quick-form/folder-quick-form.component';
import { GroupQuickFormComponent } from '@modules/elements/components/quick-forms/group-quick-form/group-quick-form.component';
import { MessageFolderQuickFormComponent } from '@modules/elements/components/quick-forms/message-folder-quick-form/message-folder-quick-form.component';
import { CalendarQuickFormComponent } from '@modules/elements/components/quick-forms/calendar-quick-form/calendar-quick-form.component';
import { NotebookQuickFormComponent } from '@modules/elements/components/quick-forms/notebook-quick-form/notebook-quick-form.component';
import { SheetQuickFormComponent } from '@modules/elements/components/quick-forms/sheet-quick-form/sheet-quick-form.component';
import { SheetFolderQuickFormComponent } from '@modules/elements/components/quick-forms/sheet-folder-quick-form/sheet-folder-quick-form.component';
import { ProjectQuickFormComponent } from '@modules/elements/components/quick-forms/project-quick-form/project-quick-form.component';
import { QuickFormComponent } from '@modules/elements/components/quick-forms/quick-form/quick-form.component';
import { TaskQuickFormComponent } from '@modules/elements/components/quick-forms/task-quick-form/task-quick-form.component';
import { FilesAppContextDirective } from '@modules/files/directives/app-files-app-context.directive';
import { MatIconModule } from '@angular/material/icon';
import { MailAppContextDirective } from '@modules/messages/directives/app-mail-app-context.directive';
import { NotesAppContextDirective } from '@modules/notes/directives/app-notes-app-context.directive';
import { SheetsAppContextDirective } from '@modules/sheets/directives/app-sheets-app-context.directive';
import { TaskingAppContextDirective } from '@modules/tasks/directives/app-tasking-app-context.directive';
import { ContactsContextMenuDirective } from './directives/contacts-context-menu.directive';
import { EventsContextMenuDirective } from './directives/events-context-menu.directive';
import { FilesContextMenuDirective } from './directives/files-context-menu.directive';
import { MessagesContextMenuDirective } from './directives/messages-context-menu.directive';
import { NotesContextMenuDirective } from './directives/notes-context-menu.directive';
import { SheetsContextMenuDirective } from './directives/sheets-context-menu.directive';
import { TasksContextMenuDirective } from './directives/tasks-context-menu.directive';
import { CalendarsPickerComponent } from '@modules/calendar-app/components/calendars-picker/calendars-picker.component';

@NgModule({
  imports: [
    FormsLayoutComponent,
    AppCommonModule,
    NavAddMenuComponent,
    PanelSheetCreateComponent,
    CalendarAppModule,
    CommonModule,
    ContactsModule,
    ElementsModule,
    FilesModule,
    FormControlsModule,
    FormsModule,
    FullCalendarModule,
    KnotsModule,
    KnowledgeModule,
    MatIconModule,
    MatTooltipModule,
    MessagesModule,
    NotesModule,
    SheetsModule,
    // SBUX-1043: the panel's own "Import spreadsheet" dialog, the same standalone one the nav bar uses.
    ImportDialogComponent,
    ContextMenuItemComponent,
    ContextMenuStateIndicatorComponent,
    PopoverDirective,
    SearchStateIndicatorComponent,
    SearchModule,
    SplitViewModule,
    TasksModule,
    UserAvatarModule,
    UtilsModule,
    GroupQuickFormComponent,
    QuickFormComponent,
    FolderQuickFormComponent,
    MessageFolderQuickFormComponent,
    CalendarQuickFormComponent,
    NotebookQuickFormComponent,
    SheetQuickFormComponent,
    SheetFolderQuickFormComponent,
    TaskQuickFormComponent,
    ProjectQuickFormComponent,
    MailSearchPopoverComponent,
    FilesSearchPopoverComponent,
    CalendarSearchPopoverComponent,
    ContactsSearchPopoverComponent,
    TaskingSearchPopoverComponent,
    NotesSearchPopoverComponent,
    SheetsSearchPopoverComponent,
    StickyEventsListComponent,
    MailAppContextDirective,
    CalendarAppContextDirective,
    TaskingAppContextDirective,
    NotesAppContextDirective,
    SheetsAppContextDirective,
    ContactsAppContextDirective,
    FilesAppContextDirective,
    CalendarsPickerComponent,
    LazyStitchyPanelComponent,
    // Team Chat in the side panel (#1447 S27): thor's panel host, standalone, in the panel's 'dp' subtree.
    LazySpaceChatPanelComponent,
    StitchyLauncherComponent,
    IconComponent,
    NoContentStubComponent,
    BoardsPanelComponent,
    AtlasPanelOutletComponent,
    LazySlidesViewComponent,
    // SBUX-1043: the Sheets panel's labelled "Back to {view}" row, the control the Slides panel uses for its own.
    Button2Component,
    SheetsPanelWorkbookComponent,
  ],
  declarations: [
    // Components
    ContactsContextMenuComponent,
    ContactsPanelComponent,
    DynamicPanelComponent,
    EventsContextMenuComponent,
    EventsPanelComponent,
    FilesContextMenuComponent,
    FilesPanelComponent,
    FormsContainerComponent,
    MessagesContextMenuComponent,
    MessagesPanelComponent,
    NotesContextMenuComponent,
    NotesPanelComponent,
    SheetsContextMenuComponent,
    SheetsPanelComponent,
    TasksContextMenuComponent,
    TasksPanelComponent,

    // Directives
    ContactsContextMenuDirective,
    EventsContextMenuDirective,
    FilesContextMenuDirective,
    MessagesContextMenuDirective,
    NotesContextMenuDirective,
    SheetsContextMenuDirective,
    TasksContextMenuDirective,
  ],
  exports: [DynamicPanelComponent],
  providers: [],
})
export class DynamicPanelModule {}
